import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import * as Icons from "lucide-react";
import { navLinks, logoUrl, services } from "../mock";

const ServiceIcon = ({ name, className }) => {
  const Icon = Icons[name] || Icons.Sparkles;
  return <Icon className={className} strokeWidth={2.1} />;
};

const Logo = () => (
  <a href="/" className="flex items-center select-none">
    <div className="h-[50px] sm:h-[56px] overflow-hidden flex items-center">
      <img
        src={logoUrl}
        alt="M.A.G Facility Services UG"
        className="h-[104px] sm:h-[116px] w-auto object-contain"
      />
    </div>
  </a>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  const closeMenu = () => {
    closeTimer.current = setTimeout(() => setServicesOpen(false), 120);
  };

  return (
    <header
      id="top"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || servicesOpen
          ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-[74px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-9">
            {navLinks.map((l) =>
              l.label === "Leistungen" ? (
                <div
                  key={l.href}
                  className="relative"
                  onMouseEnter={openMenu}
                  onMouseLeave={closeMenu}
                >
                  <a
                    href={l.href}
                    className="inline-flex items-center gap-1 text-[15px] font-medium text-slate-700 hover:text-blue-600 transition-colors"
                    data-testid="nav-leistungen-trigger"
                  >
                    {l.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                    />
                  </a>

                  {servicesOpen && (
                    <div
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
                      data-testid="leistungen-dropdown"
                    >
                      <div className="w-[640px] bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-100 p-3 grid grid-cols-2 gap-1">
                        {services.map((s) => (
                          <a
                            key={s.slug}
                            href={`/leistungen/${s.slug}`}
                            className="group flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-blue-50 transition-colors"
                            data-testid={`dropdown-service-${s.slug}`}
                          >
                            <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-50 group-hover:bg-blue-600 flex items-center justify-center transition-colors">
                              <ServiceIcon
                                name={s.icon}
                                className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors"
                              />
                            </span>
                            <span className="text-[14px] font-medium text-slate-700 group-hover:text-blue-700 transition-colors leading-tight">
                              {s.title}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  className="text-[15px] font-medium text-slate-700 hover:text-blue-600 transition-colors"
                >
                  {l.label}
                </a>
              )
            )}
          </nav>

          <div className="hidden lg:block">
            <a
              href="/#kontakt"
              className="inline-flex items-center px-5 py-2.5 rounded-lg bg-blue-600 text-white text-[15px] font-semibold shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-colors"
            >
              Angebot anfordern
            </a>
          </div>

          <button
            className="lg:hidden text-slate-800 p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menü"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-4 flex flex-col gap-1">
            {navLinks.map((l) =>
              l.label === "Leistungen" ? (
                <div key={l.href} className="border-b border-slate-50">
                  <button
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    className="w-full flex items-center justify-between py-3 text-[16px] font-medium text-slate-700"
                    data-testid="mobile-nav-leistungen-trigger"
                  >
                    {l.label}
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {mobileServicesOpen && (
                    <div className="pb-3 pl-2 flex flex-col">
                      {services.map((s) => (
                        <a
                          key={s.slug}
                          href={`/leistungen/${s.slug}`}
                          onClick={() => setOpen(false)}
                          className="flex items-center gap-3 py-2.5 text-[15px] text-slate-600 hover:text-blue-600"
                          data-testid={`mobile-dropdown-service-${s.slug}`}
                        >
                          <ServiceIcon name={s.icon} className="w-4 h-4 text-blue-600" />
                          {s.title}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="py-3 text-[16px] font-medium text-slate-700 hover:text-blue-600 border-b border-slate-50"
                >
                  {l.label}
                </a>
              )
            )}
            <a
              href="/#kontakt"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold"
            >
              Angebot anfordern
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
